import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';

import { Link } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

/* Per-status UI metadata in one place */
const STATUS_META = {
  Open: { color: '#ef4444', bg: 'danger', icon: '🔴', step: 1 },
  'In Progress': { color: '#f59e0b', bg: 'warning', icon: '🛠️', step: 2 },
  Closed: { color: '#22c55e', bg: 'success', icon: '✅', step: 3 }
};

const metaFor = (status) => STATUS_META[status] || STATUS_META.Open;

/* Summary tiles shown in the hero */
const SUMMARY_ORDER = ['Open', 'In Progress', 'Closed'];

/* Three-stop progress rail on each ticket */
const STEPS = ['Opened', 'In Progress', 'Closed'];

function TicketProgress({ status }) {
  const reached = metaFor(status).step;

  return (
    <div className="mt-steps">
      {STEPS.map((label, i) => {
        const done = i + 1 <= reached;
        const current = i + 1 === reached;

        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <span className={`mt-step-line ${done ? 'is-done' : ''}`} />
            )}
            <div className={`mt-step ${done ? 'is-done' : ''} ${current ? 'is-current' : ''}`}>
              <span className="mt-step-dot">{done ? '✓' : i + 1}</span>
              <span className="mt-step-label">{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function MyTicketsPage() {
  const [tickets, setTickets] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));

        const { data } = await axios.get(
          `${API}/tickets/user/${userInfo.email}`
        );

        setTickets(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  /* Count tickets per status for the summary tiles */
  const counts = tickets.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="mt-wrap">
          <Container>
            <div className="mt-skel mt-skel-hero mb-4" />
            <Row className="g-4">
              {[0, 1, 2].map((n) => (
                <Col xl={4} lg={6} md={6} xs={12} key={n}>
                  <div className="mt-skel mt-skel-card" />
                </Col>
              ))}
            </Row>
          </Container>
        </div>

        <style>{myTicketsStyles}</style>
      </>
    );
  }

  return (
    <>
      <CustomNavbar />

      <div className="mt-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 mt-hero">
            <span className="mt-hero-blob mt-hero-blob-1" />
            <span className="mt-hero-blob mt-hero-blob-2" />

            <Card.Body className="position-relative p-4 p-md-5">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <h1 className="fw-bold mb-1">🎫 My Tickets</h1>
                  <p className="mb-0 mt-hero-text">
                    Track the status of your support requests
                  </p>
                </div>
                <Badge className="mt-count-badge">
                  {tickets.length} {tickets.length === 1 ? 'Ticket' : 'Tickets'}
                </Badge>
              </div>

              {tickets.length > 0 && (
                <div className="mt-summary">
                  {SUMMARY_ORDER.map((status) => (
                    <div className="mt-summary-tile" key={status}>
                      <span className="mt-summary-icon">
                        {metaFor(status).icon}
                      </span>
                      <div>
                        <div className="mt-summary-num">
                          {counts[status] || 0}
                        </div>
                        <div className="mt-summary-label">{status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Ticket cards */}
          {tickets.length === 0 ? (
            <Card className="border-0 shadow-sm text-center py-5 mt-empty">
              <Card.Body>
                <div className="mt-empty-icon">🎫</div>
                <h5 className="fw-bold mt-3">No Tickets Yet</h5>
                <p className="text-muted mb-0">
                  Raise a support request from the Help page and it will appear
                  here.
                </p>
                <Button
                  as={Link}
                  to="/help"
                  className="mt-3 mt-view-btn"
                  size="sm"
                >
                  Go to Help →
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {tickets.map((ticket) => {
                const meta = metaFor(ticket.status);
                const hasReply = Boolean(ticket.adminReply);

                return (
                  <Col xl={4} lg={6} md={6} xs={12} key={ticket._id}>
                    <Card
                      className="border-0 shadow-sm h-100 mt-card"
                      style={{ '--mt-accent': meta.color }}
                    >
                      <span className="mt-card-strip" />

                      <Card.Body className="d-flex flex-column p-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="mt-id">#{ticket._id.slice(-6)}</span>
                          <Badge
                            bg={meta.bg}
                            className="rounded-pill mt-status-badge"
                          >
                            {meta.icon} {ticket.status}
                          </Badge>
                        </div>

                        <h5 className="fw-bold mt-subject mb-2">
                          {ticket.subject}
                        </h5>

                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <Badge bg="light" text="dark" className="mt-tag">
                            📦 {ticket.productName || 'N/A'}
                          </Badge>
                          {ticket.category && (
                            <Badge bg="light" text="dark" className="mt-tag">
                              🏷️ {ticket.category}
                            </Badge>
                          )}
                        </div>

                        <TicketProgress status={ticket.status} />

                        <div className="d-flex align-items-center justify-content-between mt-3 mb-3">
                          <small className="text-muted">
                            📅 {new Date(ticket.createdAt).toLocaleDateString()}
                          </small>
                          {hasReply && (
                            <small className="mt-reply-chip">💬 Replied</small>
                          )}
                        </div>

                        <Button
                          as={Link}
                          to={`/ticket/${ticket._id}`}
                          size="sm"
                          className="w-100 fw-semibold mt-auto mt-view-btn"
                        >
                          <FaEye className="me-1" /> View Ticket
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{myTicketsStyles}</style>
    </>
  );
}

const myTicketsStyles = `
  .mt-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 48px) 0;
  }

  /* Header */
  .mt-hero {
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #7c2d12 60%, #ea580c);
    position: relative;
  }
  .mt-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
  }
  .mt-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 20%;
    animation: mtFloat 9s ease-in-out infinite;
  }
  .mt-hero-blob-2 {
    width: 180px; height: 180px;
    background: #38bdf8;
    bottom: -90px; right: 8%;
    opacity: 0.3;
    animation: mtFloat 11s ease-in-out infinite reverse;
  }
  .mt-hero-text { opacity: 0.85; }
  .mt-count-badge {
    background: rgba(255,255,255,0.18) !important;
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 30px;
    padding: 10px 18px;
    font-size: 1rem;
    font-weight: 600;
  }

  /* Summary tiles inside the hero */
  .mt-summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 26px;
  }
  .mt-summary-tile {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 16px;
    padding: 14px 16px;
    backdrop-filter: blur(6px);
    transition: transform 0.2s ease, background 0.2s ease;
  }
  .mt-summary-tile:hover {
    transform: translateY(-3px);
    background: rgba(255,255,255,0.18);
  }
  .mt-summary-icon { font-size: 1.6rem; line-height: 1; }
  .mt-summary-num { font-size: 1.5rem; font-weight: 800; line-height: 1; }
  .mt-summary-label {
    font-size: 0.75rem;
    opacity: 0.82;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-top: 2px;
  }
  @media (max-width: 575.98px) {
    .mt-summary { grid-template-columns: 1fr; }
  }

  /* Empty state */
  .mt-empty { border-radius: 18px; }
  .mt-empty-icon {
    font-size: 3.4rem;
    animation: mtFloat 4s ease-in-out infinite;
  }

  /* Ticket cards */
  .mt-card {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .mt-card-strip {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: var(--mt-accent);
  }
  .mt-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 22px 40px rgba(194, 65, 12, 0.18) !important;
  }
  .mt-id {
    font-weight: 800;
    color: #64748b;
    font-size: 0.85rem;
  }
  .mt-status-badge { font-weight: 600; }
  .mt-subject {
    font-size: 1.05rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.6em;
  }
  .mt-tag {
    border: 1px solid #e5e7eb;
    border-radius: 30px;
    font-weight: 500;
    padding: 6px 12px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mt-reply-chip {
    color: #15803d;
    font-weight: 700;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 20px;
    padding: 2px 10px;
  }
  .mt-view-btn {
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #0f172a, #7c2d12);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .mt-view-btn:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #1e293b, #9a3412);
    box-shadow: 0 10px 20px rgba(15,23,42,0.25);
    color: #fff;
  }

  /* Status progress rail */
  .mt-steps {
    display: flex;
    align-items: center;
    margin-top: 4px;
  }
  .mt-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
  }
  .mt-step-dot {
    width: 26px; height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    background: #e5e7eb;
    color: #94a3b8;
    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
  }
  .mt-step.is-done .mt-step-dot {
    background: var(--mt-accent);
    color: #fff;
  }
  .mt-step.is-current .mt-step-dot {
    transform: scale(1.12);
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--mt-accent) 22%, transparent);
  }
  .mt-step-label {
    font-size: 0.66rem;
    font-weight: 600;
    color: #94a3b8;
    white-space: nowrap;
  }
  .mt-step.is-done .mt-step-label { color: #475569; }
  .mt-step-line {
    flex: 1 1 auto;
    height: 3px;
    border-radius: 3px;
    background: #e5e7eb;
    margin: 0 6px 22px;
    transition: background 0.3s ease;
  }
  .mt-step-line.is-done { background: var(--mt-accent); }

  @keyframes mtFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  }

  /* Loading skeletons */
  .mt-skel {
    background: linear-gradient(90deg, rgba(148,163,184,0.14) 25%, rgba(255,255,255,0.6) 37%, rgba(148,163,184,0.14) 63%);
    background-size: 400% 100%;
    animation: mtShimmer 1.4s ease infinite;
    border-radius: 18px;
  }
  .mt-skel-hero { height: 180px; }
  .mt-skel-card { height: 300px; border-radius: 16px; }
  @keyframes mtShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .mt-hero-blob, .mt-empty-icon, .mt-skel { animation: none; }
  }
`;

export default MyTicketsPage;
